import { useId, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  TimePicker as MuiTimePicker,
  type TimePickerProps as MuiTimePickerProps,
} from '@mui/x-date-pickers/TimePicker';
import Box from '../primitives/Box';
import { fieldLabelSx, outlinedFieldSx } from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';

type MuiTimePickerSlotProps = NonNullable<MuiTimePickerProps['slotProps']>;
type TextFieldSlotProps = NonNullable<MuiTimePickerSlotProps['textField']>;

export type TimePickerProps = Omit<MuiTimePickerProps, 'label' | 'slotProps'> & {
  label?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  helperText?: ReactNode;
  error?: boolean;
  sx?: SxProps<Theme>;
  slotProps?: Omit<MuiTimePickerSlotProps, 'textField'> & {
    textField?: TextFieldSlotProps;
  };
};

interface FieldLabelProps {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  id?: string;
  sx?: SxProps<Theme>;
  /** MUI InputLabel leftover. External labels sit above the field, so this is ignored. */
  shrink?: boolean;
}

const FieldLabelRoot = styled('label')({});

const FieldLabel = ({ children, required, htmlFor, id, sx }: FieldLabelProps) => (
  <FieldLabelRoot id={id} htmlFor={htmlFor} sx={mergeSx(fieldLabelSx, sx)}>
    {children}
    {required ? ' *' : null}
  </FieldLabelRoot>
);

/**
 * Time picker with label above the field (no floating InputLabel).
 * Relies on app-level LocalizationProvider.
 */
const TimePicker = ({
  label,
  required = false,
  fullWidth = true,
  size = 'small',
  helperText,
  error,
  sx,
  slotProps,
  ...props
}: TimePickerProps) => {
  const inputId = useId();
  const {
    textField: textFieldSlotProps,
    actionBar: actionBarSlotProps,
    ...restSlotProps
  } = slotProps ?? {};
  const textFieldObject =
    textFieldSlotProps && typeof textFieldSlotProps === 'object' ? textFieldSlotProps : undefined;
  const actionBarObject =
    actionBarSlotProps && typeof actionBarSlotProps === 'object' ? actionBarSlotProps : undefined;

  const picker = (
    <MuiTimePicker
      {...props}
      ampm={false}
      closeOnSelect
      slotProps={{
        ...restSlotProps,
        actionBar: {
          ...actionBarObject,
          actions: [],
        },
        textField: {
          id: inputId,
          fullWidth,
          size,
          helperText,
          error,
          ...textFieldObject,
          sx: mergeSx(
            outlinedFieldSx,
            textFieldObject?.sx,
            fullWidth ? { width: '100%' } : undefined,
          ),
        },
      }}
    />
  );

  if (label == null) {
    return <Box sx={mergeSx(fullWidth ? { width: '100%' } : undefined, sx)}>{picker}</Box>;
  }

  return (
    <Box
      sx={mergeSx(
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: fullWidth ? '100%' : undefined,
        },
        sx,
      )}
    >
      <FieldLabel htmlFor={inputId} required={required}>
        {label}
      </FieldLabel>
      {picker}
    </Box>
  );
};

export default TimePicker;
